import type { CategoryDto } from '@_/api-contracts/common.ts';
import type { CourseListFilterSchema } from '#src/components/CourseListFilter/index.ts';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { VscSearchStop } from 'react-icons/vsc';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { FiltersButton, LoadMoreTrigger } from '@_/components';
import { useDialog } from '@_/hooks';
import { emptyCommonI18n, quantityCommonI18n } from '@_/i18n-common';
import { EmptyState, Heading, Skeleton, SkeletonHeading, SkeletonText, Text } from '@_/ui';
import {
  getInfiniteReactQueryOptions,
  getItemsInInfiniteQuery,
  getReactQueryOptions
} from '@_/utils-frontend';
import { publicApiQueryClient } from '#src/api.ts';
import { AppShell } from '#src/components/AppShell/index.ts';
import { CourseList, CourseListSkeleton } from '#src/components/CourseList/index.ts';
import {
  CourseListFilterDialog,
  CourseListFilterMenu,
  courseListFilterSchema
} from '#src/components/CourseListFilter/index.ts';
import { Page } from '#src/components/Page/index.ts';
import { SearchPageHeader } from '#src/components/SearchPageHeader/index.ts';
import { headingI18n } from '#src/i18nMessages/index.ts';

export const coursePageSearchValidator = z.object({
  ...courseListFilterSchema.shape,
  courseType: z.enum(['online', 'offline', 'all']).optional().default('all').catch('all'),
  search: z.string().trim().nonempty().optional().catch(undefined),
  topic: z.string().trim().nonempty().optional().catch(undefined)
});

type CoursePageSearchParams = z.input<typeof coursePageSearchValidator>;

export function CoursesPage({
  category,
  onUpdateSearchParams,
  searchParams
}: {
  category?: CategoryDto;
  onUpdateSearchParams: (updater: (prev: CoursePageSearchParams) => CoursePageSearchParams) => void;
  searchParams: CoursePageSearchParams;
}) {
  const coursesQuery = useInfiniteQuery(
    getInfiniteReactQueryOptions(publicApiQueryClient.courses.list, {
      query: {
        academicLevelIds: searchParams.academicLevelIds?.length
          ? searchParams.academicLevelIds
          : undefined,
        categoryId: category?.id,
        countryZoneCodes: searchParams.countryZoneCodes?.length
          ? searchParams.countryZoneCodes
          : undefined,
        curriculumIds: searchParams.curriculumIds?.length ? searchParams.curriculumIds : undefined,
        name: searchParams.search,
        topic: searchParams.topic
      }
    })
  );

  const { items: courses, totalItems } = getItemsInInfiniteQuery(coursesQuery.data);

  const topicQuery = useQuery({
    ...getReactQueryOptions(publicApiQueryClient.topics.get, {
      params: { slug: searchParams.topic! }
    }),
    enabled: !!searchParams.topic
  });

  const topic = topicQuery.data?.data;

  const handleChangeFilter = useCallback(
    (updatedFilter: CourseListFilterSchema) => {
      onUpdateSearchParams(prev => ({
        ...prev,
        academicLevelIds: updatedFilter.academicLevelIds?.length
          ? updatedFilter.academicLevelIds
          : undefined,
        countryZoneCodes: updatedFilter.countryZoneCodes?.length
          ? updatedFilter.countryZoneCodes
          : undefined,
        curriculumIds: updatedFilter.curriculumIds?.length ? updatedFilter.curriculumIds : undefined
      }));
    },
    [onUpdateSearchParams]
  );

  const filterDialog = useDialog({
    component: CourseListFilterDialog,
    props: {
      onSuccess: handleChangeFilter,
      showCurriculumFilter: category?.hasAcademicLevels ?? true,
      value: searchParams
    }
  });

  const actionsMarkup = (
    <div className="lg:hidden">
      <FiltersButton onClick={() => filterDialog.open()} />
    </div>
  );

  let heading: React.ReactNode;
  if (!searchParams.search) {
    if (searchParams.topic) {
      if (topic) {
        heading = (
          <FormattedMessage
            id="heading.classes-by-topic"
            defaultMessage="{topic} Classes"
            values={{ topic: topic.name }}
          />
        );
      }
    } else if (category) {
      heading = (
        <FormattedMessage
          id="heading.classes-by-category"
          defaultMessage="{category} Classes"
          values={{ category: category.name }}
        />
      );
    } else {
      heading = <FormattedMessage {...headingI18n.classes} />;
    }
  }

  return (
    <>
      {filterDialog.element}

      <AppShell>
        <Page>
          {searchParams.search ? (
            <SearchPageHeader
              actions={actionsMarkup}
              currentTab="courses"
              searchStr={searchParams.search}
            />
          ) : (
            <header className="lg:border-b lg:border-gray-200">
              {heading ? (
                <Heading>{heading}</Heading>
              ) : (
                <Skeleton>
                  <SkeletonHeading className="max-w-72" />
                </Skeleton>
              )}

              <div className="flex items-center justify-between py-4 lg:py-3">
                {coursesQuery.isPending ? (
                  <>
                    <Skeleton>
                      <SkeletonText className="max-w-20" />
                    </Skeleton>
                  </>
                ) : (
                  <>
                    <Text block subdued>
                      <FormattedMessage
                        {...quantityCommonI18n.numResults}
                        values={{ num: totalItems }}
                      />
                    </Text>
                    {actionsMarkup}
                  </>
                )}
              </div>
            </header>
          )}

          <div className="my-4">
            <div className="lg:grid lg:grid-cols-4 lg:gap-x-12">
              <div className="hidden lg:col-span-1 lg:block lg:py-4">
                <CourseListFilterMenu
                  disabled={coursesQuery.isPending}
                  onChange={handleChangeFilter}
                  showCurriculumFilter={category?.hasAcademicLevels ?? true}
                  value={searchParams}
                />
              </div>
              <div className="lg:col-span-3 lg:mt-4">
                {coursesQuery.isPending && <CourseListSkeleton numColumns={3} />}

                {!coursesQuery.isPending && !courses.length && (
                  <EmptyState
                    heading={<FormattedMessage {...emptyCommonI18n.searchResultsPolite} />}
                    icon={<VscSearchStop />}
                  />
                )}

                {!!courses.length && (
                  <>
                    <CourseList items={courses} numColumns={3} />

                    <LoadMoreTrigger
                      className="my-6"
                      hasMore={coursesQuery.hasNextPage}
                      loading={coursesQuery.isFetchingNextPage}
                      onFetchMore={coursesQuery.fetchNextPage}
                      renderLoading={() => <CourseListSkeleton numColumns={3} />}
                      trigger="scroll"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </Page>
      </AppShell>
    </>
  );
}
